import { signIn } from 'next-auth/react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Text from '../atoms/Text';
import OtpVerification from '../organisms/OtpVerification';
import SignIn from '../organisms/SignIn';
import SignUp from '../organisms/SignUp';
import { event } from '../services/gtag.service';

const UserAuthTemplate = ({ title = 'Find climate capital', ...property }) => {
    const router = useRouter();
    const [OTP, setOTP] = useState(-1);
    const [isSignInForm, setIsSignInForm] = useState(true);
    const [step, setStep] = useState(0);
    const [isOTPError, setIsOTPError] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        show: false,
        message: ''
    });
    const [isLoading, setIsLoading] = useState(false);

    const [email, setEmail] = useState('');

    const options = [
        { value: 'Climate Entrepreneur', label: 'Climate Entrepreneur' },
        { value: 'Climate Funder', label: 'Climate Funder' },
        { value: 'Climate Enabler', label: 'Climate Enabler' },
        { value: 'Others', label: 'Others' }
    ];

    const [defaultEmail, setDefaultEmail] = useState('');
    const toggleSignUpAndSignIn = (currentEmail) => {
        setIsSignInForm(!isSignInForm);
        setDefaultEmail(currentEmail);
    };

    const signInHandler = async (email) =>
        fetch('/api/signin', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                email
            })
        }).then((data) => data.json());

    const handleResendOTP = async () => {
        event(
            {
                action: 'resend-otp'
            },
            { 'user-id': email }
        );
        const response = await signInHandler(email);
        if (response.status) {
            event(
                {
                    action: 'resend-otp'
                },
                { 'user-id': email, status: 'success' }
            );
        } else {
            event(
                {
                    action: 'resend-otp'
                },
                { 'user-id': email, status: 'failure' }
            );
            setErrorMessage((prev) => ({
                ...prev,
                show: true,
                message: response.message
            }));
            setTimeout(() => {
                setErrorMessage({});
            }, 5000);
        }
    };

    const handleSignInFormSubmit = async (formValues) => {
        setIsLoading(true);
        event(
            {
                action: 'login-btn-clicked',
                category: 'Button',
                label: 'Sign In'
            },
            { 'btn-clicked': 'yes' }
        );
        const { email } = formValues;
        setEmail(email);
        const response = await signInHandler(email);
        if (response.status) {
            setStep((prev) => prev + 1);
        } else {
            setErrorMessage((prev) => ({
                ...prev,
                show: true,
                message: response.message
            }));
            setTimeout(() => {
                setErrorMessage({});
            }, 5000);
        }
        setIsLoading(false);
        return response;
    };

    const handleFormSubmit = async (formValues) => {
        setIsLoading(true);
        event(
            {
                action: 'signup-btn-clicked'
            },
            { 'btn-clicked': 'yes' }
        );
        const {
            companyName,
            name,
            email,
            companyWebsite,
            userType: { value: userTypeValue },
            agreedTerms
        } = formValues;
        const response = await fetch('/api/signup', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                companyName,
                name,
                email,
                companyWebsite,
                userType: userTypeValue,
                agreedTerms
            })
        }).then((data) => data.json());

        if (response.status) {
            setStep((prev) => prev + 1);
            setEmail(email);
            event(
                {
                    action: 'signup-attempt'
                },
                {
                    'email-id': email,
                    'full-name': name,
                    'user-role': userTypeValue
                }
            );
        } else {
            setErrorMessage((prev) => ({
                ...prev,
                show: true,
                message: response.message
            }));
            setTimeout(() => {
                setErrorMessage({});
            }, 5000);
        }
        setIsLoading(false);
    };

    const handleOTPSubmit = async (e) => {
        e.preventDefault();
        if (isSignInForm) {
            event(
                {
                    action: 'login-attempt'
                },
                { 'user-id': email }
            );
        }
        setIsLoading(true);
        const res = await signIn('credentials', {
            email,
            otp: OTP,
            redirect: false,
            isSignInForm
        });
        if (res.ok) {
            router.replace('/');
            event(
                {
                    action: 'login-successful'
                },
                { 'user-id': email, status: 'success' }
            );
        } else {
            setIsOTPError(true);
            event(
                {
                    action: 'login-unsuccessful'
                },
                { 'user-id': email, status: 'failure' }
            );
        }
        setIsLoading(false);
    };

    useEffect(() => {
        document.body.style.overflow = 'visible';
    }, []);
    return (
        <div
            className={`w-full px-4 lg:px-0 h-full flex justify-center ${
                isSignInForm ? 'pt-14 lg:pt-36' : 'pt-20'
            } pb-10 ${property.className}`}
        >
            <div className="flex flex-col w-full md:w-1/2 lg:w-1/3 xl:w-1/4 z-50">
                <div className="flex flex-col items-center space-y-6 md:space-y-8">
                    <div className="relative w-44 h-32">
                        <Image
                            src="/images/climateXcapital.png"
                            alt="logo"
                            fill
                        />
                    </div>
                    <Text
                        className="text-2xl md:text-3xl text-neutral-800"
                        fontWeight="font-medium"
                    >
                        {title}
                    </Text>
                </div>
                {step <= 0 && (
                    <>
                        {isSignInForm ? (
                            <SignIn
                                handleSignInFormSubmit={handleSignInFormSubmit}
                                setStep={setStep}
                                isLoading={isLoading}
                                errorMessage={errorMessage}
                                toggleSignUpAndSignIn={toggleSignUpAndSignIn}
                                defaultEmail={defaultEmail}
                            />
                        ) : (
                            <SignUp
                                defaultEmail={defaultEmail}
                                handleFormSubmit={handleFormSubmit}
                                options={options}
                                isLoading={isLoading}
                                errorMessage={errorMessage}
                                toggleSignUpAndSignIn={toggleSignUpAndSignIn}
                            />
                        )}
                    </>
                )}
                {step === 1 && (
                    <form onSubmit={handleOTPSubmit}>
                        <OtpVerification
                            setOTP={setOTP}
                            email={email}
                            isLoading={isLoading}
                            isOTPError={isOTPError}
                            setIsOTPError={setIsOTPError}
                            handleResend={handleResendOTP}
                        />
                    </form>
                )}
            </div>
        </div>
    );
};

export default UserAuthTemplate;

UserAuthTemplate.propTypes = {};
